import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import DatePickerBox from "react-date-picker";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { GoArrowDown } from "react-icons/go";
import { RxCalendar } from "react-icons/rx";
import styled from "styled-components";
import cn from "../../lib/cn";
import H3 from "../../shared/typography/H3";
import P from "../../shared/typography/P";
import Dropdown from "../../shared/ui/Dropdown/Dropdown";
const tickAmountMap = {
    Day: 9,
    "One Week": 7,
    "One Month": 15,
    "One Year": 12,
    Lifetime: 8,
};
const formatSeriesData = (data) => {
    return data.map((dataset) => ({
        name: dataset.title || "Power Generation",
        data: dataset.graphData
            ?.slice()
            .sort(
                (a, b) => parseInt(a.timestamp, 10) - parseInt(b.timestamp, 10)
            )
            .map((dataPoint) => {
                const timestamp = parseInt(dataPoint.timestamp, 10) * 1000;
                const date = new Date(timestamp);

                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                const seconds = String(date.getSeconds()).padStart(2, "0");

                return {
                    x: `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`,
                    y: parseFloat(dataPoint.value)
                        .toFixed(1)
                        .replace(/\.?0+$/, ""),
                };
            }),
    }));
};

const Graph = ({
    genData,
    data,
    type,
    onDateChange,
    endDate,
    updateData,
    selection,
    projectEnable,
}) => {
    const series = useMemo(() => formatSeriesData(data), [data]);
    const currentendDate = endDate;
    const currentDate = new Date();
    const options = useMemo(
        () => ({
            chart: {
                id: "area-datetime",
                type: type,
                height: 350,
                toolbar: { show: false },
                zoom: {
                    enabled: true,
                    type: "x",
                },
                pan: {
                    enabled: true,
                    mode: "x",
                },
            },
            stroke: {
                curve: "smooth",
            },
            legend: {
                enabled: false,
            },
            dataLabels: {
                enabled: false,
            },
            colors: projectEnable ? undefined : ["#B0B0B0"],
            xaxis: {
                type: "datetime",
                tickAmount: 9,
                labels: {
                    formatter: (value) => {
                        const date = new Date(value);
                        return date.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                        });
                    },
                },
            },
        }),
        [type, projectEnable]
    );

    const [state, setState] = useState({
        series,
        options,
    });
    const isDisabled =
        currentendDate.setHours(0, 0, 0, 0) ===
            currentDate.setHours(0, 0, 0, 0) || !projectEnable;

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            series: formatSeriesData(data),
            options: {
                ...prevState.options,
                chart: {
                    ...prevState.options.chart,
                    type: type,
                },
                colors: projectEnable ? undefined : ["#B0B0B0"],
                xaxis: {
                    ...prevState.options.xaxis,
                    labels: {
                        formatter: (value) => {
                            const date = new Date(value);
                            return selection === "Day"
                                ? date.toLocaleTimeString("en-GB", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                  })
                                : selection === "One Week"
                                ? date.toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                  })
                                : date.toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                  });
                        },
                    },
                    tickAmount: tickAmountMap[selection] ?? 4,
                },
            },
        }));
    }, [type, data, selection, projectEnable]);
    const changeDateBy = (days) => {
        const newDate = new Date(endDate);
        newDate.setDate(newDate.getDate() + days);
        onDateChange(newDate);
    };

    return (
        <div className="bg-gray-100 w-full p-1 rounded-xl">
            <div className="bg-white p-1">
                <div className="flex flex-wrap  justify-between gap-2 items-center">
                    <div className="flex items-center gap-3">
                        {genData?.map((item, index) => (
                            <div key={index}>
                                <P
                                    className={cn({
                                        "text-gray-130": !projectEnable,
                                    })}
                                >
                                    {item.label}
                                </P>
                                <H3
                                    className={cn(item.color, {
                                        "text-gray-130": !projectEnable,
                                    })}
                                >
                                    {item.value}
                                </H3>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <Dropdown
                            selectstyle="text-nowrap"
                            selected={selection}
                            onSelect={(v) => {
                                updateData(v);
                            }}
                            disabled={!projectEnable}
                            data={[
                                "Day",
                                "One Week",
                                "One Month",
                                "One Year",
                                "Lifetime",
                            ]}
                            icon={<GoArrowDown />}
                        />
                        <div
                            className={` relative px-px py-px  border w-[70%]   rounded-lg border-[#DCDCDC] text-btn-s`}
                        >
                            <DatePickerWrapper>
                                <div
                                    className={cn(
                                        "flex items-center justify-between gap-1",
                                        { "bg-[#f0f0f0]": !projectEnable }
                                    )}
                                >
                                    <button
                                        disabled={!projectEnable}
                                        className={cn("text-sm", {
                                            " cursor-not-allowed text-gray-140":
                                                !projectEnable,
                                        })}
                                        onClick={() => changeDateBy(-1)}
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <DatePickerBox
                                        onChange={onDateChange}
                                        value={endDate}
                                        dayPlaceholder="dd"
                                        maxDate={new Date()}
                                        name="date"
                                        disabled={!projectEnable}
                                        monthPlaceholder="mm"
                                        format="dd/MM/yyyy"
                                        yearPlaceholder="yyyy"
                                        clearIcon={null}
                                        calendarIcon={<RxCalendar size={20} />}
                                        className={`w-full flex border-none rounded-lg text-sm  text-primary bg-white h-[40px] md:h-[40px]`}
                                        calendarClassName="relative top-1 !border-2 !border-blue-400 bg-white rounded-lg"
                                        wrapperClassName={`bg-white !border-none w-full`}
                                        inputClassName="text-primary text-xs border-none w-full"
                                    />
                                    <button
                                        disabled={isDisabled}
                                        className={cn("text-sm", {
                                            "cursor-not-allowed text-gray-140":
                                                isDisabled,
                                        })}
                                        onClick={() => changeDateBy(1)}
                                    >
                                        <FaChevronRight />
                                    </button>
                                </div>
                            </DatePickerWrapper>
                        </div>
                    </div>
                </div>
                <div>
                    <ReactApexChart
                        options={state.options}
                        series={state.series}
                        type={type}
                        height={document.body.clientWidth < 500 ? 350 : 370}
                    />
                </div>
            </div>
        </div>
    );
};

export default Graph;

const DatePickerWrapper = styled.div`
    .react-calendar {
        font-family: Urbanist, sans serif;
    }

    .react-date-picker__wrapper {
        border: none !important;
    }
    .react-date-picker {
        height: 40px;
    }
    @media (min-width: 1024px) {
        .react-date-picker {
            height: 47px;
        }
    }

    @media (min-width: 1536px) {
        .react-date-picker {
            height: 53px;
        }
    }
    .react-calendar__tile {
        color: #171d39;
        font-size: 1.5rem;
    }
    .react-calendar__tile:disabled {
        background-color: #dbdbdc !important;
    }
    .react-calendar__tile--now {
        background-color: white !important;
    }
    .react-calendar__tile--active {
        background-color: #171d39 !important;
        color: white;
        border-radius: 55%;
    }
    .react-calendar__navigation button:disabled {
        background-color: white !important;
    }
    .react-calendar__navigation__prev2-button {
        display: none;
    }
    .react-calendar__navigation__next2-button {
        display: none;
    }
    abbr[title] {
        text-decoration: none;
        color: #b5bec6;
        font-size: 1rem;
        font-weight: 500;
    }
`;
